/**
 * Crossword generation algorithm.
 *
 * Pure / deterministic with a seed. Takes a list of (word, clue) pairs and
 * tries to interlock them into a compact grid.
 *
 * Returns a normalized grid bounded to its bounding box, with numbering and
 * the across/down clue lists.
 */

export interface CrosswordInputWord {
  word: string;
  clue: string;
}

export interface CrosswordCell {
  row: number;
  col: number;
  letter: string;
  /** Number printed in the cell (only for cells starting a word). */
  number?: number;
}

export interface CrosswordPlacedClue {
  number: number;
  clue: string;
  word: string;
  /** Original word as the user typed it (with accents/case). */
  display: string;
  row: number;
  col: number;
  length: number;
}

export interface CrosswordGrid {
  rows: number;
  cols: number;
  cells: CrosswordCell[];
  across: CrosswordPlacedClue[];
  down: CrosswordPlacedClue[];
  /** Words that could not be placed (kept in original casing). */
  unplaced: CrosswordInputWord[];
}

type Direction = 'across' | 'down';

interface InternalPlacement {
  word: string;          // normalized (A-Z)
  display: string;       // original
  clue: string;
  row: number;
  col: number;
  direction: Direction;
}

/** Strip diacritics, keep A-Z only, uppercase. */
export function normalizeWord(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');
}

/**
 * Build a crossword grid.
 *
 * Algorithm: classic "crossword compiler" greedy with scoring.
 * - Sort words by length DESC.
 * - Place first word horizontally at origin.
 * - For each next word, try every letter against every existing cell with the
 *   same letter; place perpendicular if valid (no collision, no parasite
 *   adjacency). Score = number of intersections + compactness bonus.
 * - Keep the best placement, repeat until all words processed.
 */
export function generateCrossword(words: CrosswordInputWord[]): CrosswordGrid {
  // Normalize and de-duplicate, keep order stable.
  const cleaned: { word: string; display: string; clue: string }[] = [];
  const seen = new Set<string>();
  for (const w of words) {
    const norm = normalizeWord(w.word);
    if (norm.length < 2 || seen.has(norm)) continue;
    seen.add(norm);
    cleaned.push({ word: norm, display: w.word.trim(), clue: w.clue.trim() });
  }

  // Sort by length DESC, ties keep insertion order.
  cleaned.sort((a, b) => b.word.length - a.word.length);

  const placements: InternalPlacement[] = [];
  const unplaced: CrosswordInputWord[] = [];

  // Use a sparse map keyed by "r,c" -> letter.
  const grid = new Map<string, string>();
  const key = (r: number, c: number) => `${r},${c}`;

  const setCell = (r: number, c: number, ch: string) => grid.set(key(r, c), ch);
  const getCell = (r: number, c: number) => grid.get(key(r, c));

  const placeFirst = (entry: { word: string; display: string; clue: string }) => {
    for (let i = 0; i < entry.word.length; i++) setCell(0, i, entry.word[i]);
    placements.push({ ...entry, row: 0, col: 0, direction: 'across' });
  };

  /** Check that placing `word` at (r,c) in `dir` is valid. Returns intersection count or -1. */
  const tryPlacement = (word: string, r: number, c: number, dir: Direction): number => {
    let intersections = 0;
    const dr = dir === 'down' ? 1 : 0;
    const dc = dir === 'across' ? 1 : 0;

    // Cell BEFORE the word must be empty.
    const beforeR = r - dr;
    const beforeC = c - dc;
    if (getCell(beforeR, beforeC) !== undefined) return -1;

    // Cell AFTER the word must be empty.
    const afterR = r + dr * word.length;
    const afterC = c + dc * word.length;
    if (getCell(afterR, afterC) !== undefined) return -1;

    for (let i = 0; i < word.length; i++) {
      const rr = r + dr * i;
      const cc = c + dc * i;
      const existing = getCell(rr, cc);
      if (existing !== undefined) {
        if (existing !== word[i]) return -1;
        intersections++;
      } else {
        // Sides must be empty (no parasite adjacency) for non-intersection cells.
        if (dir === 'across') {
          if (getCell(rr - 1, cc) !== undefined) return -1;
          if (getCell(rr + 1, cc) !== undefined) return -1;
        } else {
          if (getCell(rr, cc - 1) !== undefined) return -1;
          if (getCell(rr, cc + 1) !== undefined) return -1;
        }
      }
    }
    if (intersections === 0) return -1; // must cross at least one existing letter
    return intersections;
  };

  const commitPlacement = (
    entry: { word: string; display: string; clue: string },
    r: number,
    c: number,
    dir: Direction
  ) => {
    const dr = dir === 'down' ? 1 : 0;
    const dc = dir === 'across' ? 1 : 0;
    for (let i = 0; i < entry.word.length; i++) {
      setCell(r + dr * i, c + dc * i, entry.word[i]);
    }
    placements.push({ ...entry, row: r, col: c, direction: dir });
  };

  // Bounding-box compactness scoring helper.
  const currentBounds = () => {
    let minR = Infinity, maxR = -Infinity, minC = Infinity, maxC = -Infinity;
    for (const k of grid.keys()) {
      const [r, c] = k.split(',').map(Number);
      if (r < minR) minR = r;
      if (r > maxR) maxR = r;
      if (c < minC) minC = c;
      if (c > maxC) maxC = c;
    }
    return { minR, maxR, minC, maxC };
  };

  for (let idx = 0; idx < cleaned.length; idx++) {
    const entry = cleaned[idx];
    if (placements.length === 0) {
      placeFirst(entry);
      continue;
    }

    // Find all candidate placements.
    let best: { r: number; c: number; dir: Direction; score: number } | null = null;

    for (let i = 0; i < entry.word.length; i++) {
      const letter = entry.word[i];
      for (const placed of placements) {
        for (let j = 0; j < placed.word.length; j++) {
          if (placed.word[j] !== letter) continue;

          // Anchor cell coords of the already-placed letter.
          const ar = placed.row + (placed.direction === 'down' ? j : 0);
          const ac = placed.col + (placed.direction === 'across' ? j : 0);

          // New word goes perpendicular.
          const newDir: Direction = placed.direction === 'across' ? 'down' : 'across';
          const r = newDir === 'down' ? ar - i : ar;
          const c = newDir === 'across' ? ac - i : ac;

          const inter = tryPlacement(entry.word, r, c, newDir);
          if (inter <= 0) continue;

          // Compactness: prefer placements that minimize bounding-box growth.
          const b = currentBounds();
          const endR = newDir === 'down' ? r + entry.word.length - 1 : r;
          const endC = newDir === 'across' ? c + entry.word.length - 1 : c;
          const newW = Math.max(b.maxC, endC) - Math.min(b.minC, c) + 1;
          const newH = Math.max(b.maxR, endR) - Math.min(b.minR, r) + 1;
          const score = inter * 100 - (newW + newH);

          if (!best || score > best.score) {
            best = { r, c, dir: newDir, score };
          }
        }
      }
    }

    if (best) {
      commitPlacement(entry, best.r, best.c, best.dir);
    } else {
      unplaced.push({ word: entry.display, clue: entry.clue });
    }
  }

  // Normalize to bounding box.
  if (grid.size === 0) {
    return { rows: 0, cols: 0, cells: [], across: [], down: [], unplaced };
  }
  const b = currentBounds();
  const rows = b.maxR - b.minR + 1;
  const cols = b.maxC - b.minC + 1;

  const cells: CrosswordCell[] = [];
  for (const [k, letter] of grid.entries()) {
    const [r, c] = k.split(',').map(Number);
    cells.push({ row: r - b.minR, col: c - b.minC, letter });
  }

  // Numbering: scan in reading order, assign a number to any cell that starts
  // an across word (left edge empty, right cell exists) or a down word
  // (top edge empty, bottom cell exists).
  const cellAt = (r: number, c: number) => cells.find((cc) => cc.row === r && cc.col === c);
  let nextNumber = 1;
  const numberByCoord = new Map<string, number>();

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const here = cellAt(r, c);
      if (!here) continue;
      const startsAcross = !cellAt(r, c - 1) && !!cellAt(r, c + 1);
      const startsDown = !cellAt(r - 1, c) && !!cellAt(r + 1, c);
      if (startsAcross || startsDown) {
        here.number = nextNumber;
        numberByCoord.set(`${r},${c}`, nextNumber);
        nextNumber++;
      }
    }
  }

  // Build clue lists from placements (translate to normalized coords).
  const across: CrosswordPlacedClue[] = [];
  const down: CrosswordPlacedClue[] = [];
  for (const p of placements) {
    const r = p.row - b.minR;
    const c = p.col - b.minC;
    const number = numberByCoord.get(`${r},${c}`) ?? 0;
    const entry: CrosswordPlacedClue = {
      number,
      clue: p.clue,
      word: p.word,
      display: p.display,
      row: r,
      col: c,
      length: p.word.length,
    };
    if (p.direction === 'across') across.push(entry);
    else down.push(entry);
  }
  across.sort((a, b) => a.number - b.number);
  down.sort((a, b) => a.number - b.number);

  return { rows, cols, cells, across, down, unplaced };
}
